import { useCallback, useEffect, useMemo, useState } from 'react'
import { doc, onSnapshot, setDoc } from 'firebase/firestore'
import { db, serverTimestamp } from '../config/firebase.js'
import { useAuth } from './useAuth.js'

// Constants for type safety and DRY principles
const NOTIFICATION_TYPES = ['off', 'instant', 'daily', 'weekly', 'monthly']
const DEFAULT_PREFERENCES = Object.freeze({
  notifications: {
    deviceAlerts: 'off',
    configUpdates: 'off',
  },
})

/**
 * Firebase v11+ User Preferences Hook
 * 
 * Manages user notification preferences with real-time Firestore sync.
 * Optimized for React 19+ with minimal re-renders and ES2022+ patterns.
 * 
 * Features:
 * - Real-time sync with server timestamps
 * - Optimized re-renders with useMemo
 * - Type-safe preference updates
 * - Consistent error handling
 */
export function useUserPreferences() {
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES)
  const [loading, setLoading] = useState(true)
  const { user } = useAuth()

  // Memoized user document reference to prevent unnecessary re-renders
  const userDocRef = useMemo(() => 
    user ? doc(db, 'users', user.uid) : null, 
    [user],
  )

  // Streamlined user document update with server timestamps
  const updateUserDocument = useCallback(async (newPrefs) => {
    if (!user || !userDocRef) return { success: false, error: 'User not authenticated' }

    try {
      await setDoc(userDocRef, {
        email: user.email,
        displayName: user.displayName || user.email,
        lastLoginAt: serverTimestamp(),
        preferences: newPrefs,
      }, { merge: true })
      
      return { success: true }
    } catch (error) {
      console.error('Error updating user preferences:', error)
      return { success: false, error: error.message }
    }
  }, [user, userDocRef])

  // Initialize user document with defaults and server timestamps
  const initializeUserDocument = useCallback(async () => {
    if (!user || !userDocRef) return

    try {
      await setDoc(userDocRef, {
        email: user.email,
        displayName: user.displayName || user.email,
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
        preferences: DEFAULT_PREFERENCES,
      }, { merge: true })
      
      setPreferences(DEFAULT_PREFERENCES)
    } catch (error) {
      console.error('Error initializing user document:', error)
      throw error
    }
  }, [user, userDocRef])

  // Real-time listener for user preferences with optimized cleanup
  useEffect(() => {
    if (!userDocRef) {
      setLoading(false)
      return
    }

    const unsubscribe = onSnapshot(
      userDocRef,
      async (docSnapshot) => {
        try {
          if (docSnapshot.exists()) {
            const userData = docSnapshot.data()
            if (userData.preferences) {
              setPreferences(userData.preferences)
            } else {
              // Initialize with defaults if preferences don't exist
              await initializeUserDocument()
            }
          } else {
            // Initialize user document if it doesn't exist
            await initializeUserDocument()
          }
        } catch (error) {
          console.error('Error processing user preferences:', error)
        } finally {
          setLoading(false)
        }
      },
      (error) => {
        console.error('Error loading user preferences:', error)
        setLoading(false)
      },
    )

    return unsubscribe
  }, [userDocRef, initializeUserDocument])

  // Update specific notification preference with validation
  const updateNotificationPreference = useCallback(async (notificationType, value) => {
    if (!NOTIFICATION_TYPES.includes(value)) {
      return { success: false, error: 'Invalid notification preference value' }
    }

    const newPrefs = {
      ...preferences,
      notifications: {
        ...preferences.notifications,
        [notificationType]: value,
      },
    }
    
    const result = await updateUserDocument(newPrefs)
    if (result.success) {
      setPreferences(newPrefs)
    }
    return result
  }, [preferences, updateUserDocument])

  // Reset to default preferences
  const resetToDefaults = useCallback(async () => {
    const result = await updateUserDocument(DEFAULT_PREFERENCES)
    if (result.success) {
      setPreferences(DEFAULT_PREFERENCES)
    }
    return result
  }, [updateUserDocument])

  return {
    preferences,
    loading,
    updatePreferences: updateUserDocument,
    updateNotificationPreference,
    resetToDefaults,
  }
}
